import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Sliders, Zap } from "lucide-react";
import { LinkInput } from "./LinkInput";
import { ResultCard } from "./ResultCard";
import type { CommentResult, Brand } from "../types";

interface DashboardViewProps {
    activeBrand: Brand | null;
    links: string[];
    results: Record<string, CommentResult>;
    count: number;
    setCount: (n: number) => void;
    isABTest: boolean;
    setIsABTest: (b: boolean) => void;
    handleUpdateLink: (index: number, val: string) => void;
    handleAddLink: () => void;
    handleRemoveLink: (index: number) => void;
    handleStartProcessing: () => void;
    onRetry: (url: string) => void;
    onRegenerate: (url: string) => void;
    onCopy: (text: string) => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({
    activeBrand,
    links,
    results,
    count,
    setCount,
    isABTest,
    setIsABTest,
    handleUpdateLink,
    handleAddLink,
    handleRemoveLink,
    handleStartProcessing,
    onRetry,
    onRegenerate,
    onCopy,
}) => {
    return (
        <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-12"
        >

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-5 space-y-6">
                    {/* Controls Section */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                                    <Sliders size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-white">Generation Settings</h3>
                                    <p className="text-xs text-white/40">Customize output behavior</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3 block">
                                    Comments per Post ({count})
                                </label>
                                <div className="flex gap-2">
                                    {[1, 2, 3].map((num) => (
                                        <button
                                            key={num}
                                            onClick={() => setCount(num)}
                                            className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all border ${count === num && !isABTest
                                                ? "bg-primary text-white border-primary shadow-lg shadow-primary/20"
                                                : "bg-white/5 text-white/40 border-white/5 hover:bg-white/10"
                                                } ${isABTest ? "opacity-50 cursor-not-allowed" : ""}`}
                                            disabled={isABTest}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={`p-4 rounded-xl border transition-all cursor-pointer ${isABTest
                                ? "bg-purple-500/10 border-purple-500/50"
                                : "bg-white/5 border-white/5 hover:bg-white/10"
                                }`}
                                onClick={() => setIsABTest(!isABTest)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-1.5 rounded-lg ${isABTest ? "bg-purple-500/20 text-purple-400" : "bg-white/10 text-white/40"}`}>
                                            <Zap size={16} />
                                        </div>
                                        <div>
                                            <div className="font-bold text-white text-sm">A/B Testing Mode</div>
                                            <div className="text-[10px] text-white/40">Generate 2 distinct variants (Hook vs. Standard)</div>
                                        </div>
                                    </div>
                                    <div className={`w-10 h-6 rounded-full p-1 transition-colors ${isABTest ? "bg-purple-500" : "bg-white/10"}`}>
                                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${isABTest ? "translate-x-4" : ""}`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LinkInput
                        links={links}
                        isProcessing={Object.values(results).some(r => r.status === "scraping" || r.status === "generating")}
                        onUpdateLink={handleUpdateLink}
                        onAddLink={handleAddLink}
                        onRemoveLink={handleRemoveLink}
                        onStartProcessing={handleStartProcessing}
                    />
                </div>
                <div className="lg:col-span-7 space-y-6">
                    <AnimatePresence mode="popLayout">
                        {Object.keys(results).length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="p-12 text-center bg-white/[0.01] border-2 border-dashed border-white/5 rounded-[2.5rem]"
                            >
                                <Instagram className="mx-auto text-white/10 mb-6" size={64} />
                                <p className="text-white/40 font-medium text-lg">Add Instagram links to begin</p>
                            </motion.div>
                        ) : (
                            Object.values(results).map((result) => (
                                <ResultCard key={result.url} result={result} onRetry={() => onRetry(result.url)} onRegenerate={() => onRegenerate(result.url)} onCopy={onCopy} />
                            ))
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

export default DashboardView;
